import clsx from "clsx";
import { SwitchField } from "common";
import { useFormikContext } from "formik";
import { get } from "lodash";
import React, { useMemo } from "react";
import { CheckInValidationType } from "types";
import { TimePickerPopover } from "@/components/Common/TimePickerPopover";
import { CheckInPreferenceFormValues } from "@/components/Product/ExperienceCheckIn/ExperienceCheckIn.types";
import { useFirstCheckInCutoffFieldValues } from "@/components/Product/ExperienceCheckIn/useFirstCheckInCutoffFieldValues";

interface MultipleCheckInFieldProps {
    disabled?: boolean;
    requireGuestIdentification?: boolean;
    values: CheckInPreferenceFormValues | null;
}

export const FirstCheckInCutoffField = ({
    disabled,
    requireGuestIdentification,
    values,
}: MultipleCheckInFieldProps) => {
    const { errors } = useFormikContext();

    const error = useMemo(() => {
        const fieldName = "checkInPreference.validations";
        const countValidationIndex =
            values?.validations.findIndex(
                (validation) => validation.object === CheckInValidationType.FIRST_CHECK_IN_CUTOFF,
            ) ?? -1;

        if (countValidationIndex === -1) return null;

        return get(errors, `${fieldName}.${countValidationIndex}.max`) ?? null;
    }, [errors, values?.validations]);

    const { duration, isEnabled, handleEnabledToggle, handleDurationChange } = useFirstCheckInCutoffFieldValues(values);

    return (
        <SwitchField
            isDisabled={disabled}
            isChecked={requireGuestIdentification ? isEnabled : false}
            data-testid="check-in-window-relative-to-first-check-in-toggle"
            onChange={handleEnabledToggle}
        >
            <span className="flex flex-wrap gap-x-1" onClick={(e) => e.stopPropagation()}>
                All subsequent check-ins must be within
                {isEnabled ? (
                    <span>
                        <TimePickerPopover
                            showArrow
                            classNames={{
                                text: clsx(
                                    "px-0.5 pb-0.5 mx-1",
                                    error ? "!text-danger !border-danger" : "!text-black !border-black",
                                ),
                            }}
                            value={duration}
                            onApply={handleDurationChange}
                        />
                        of
                    </span>
                ) : (
                    <span>a limited window after</span>
                )}
                first check-in
            </span>
        </SwitchField>
    );
};
